// @flow
import * as React from 'react';
import {
    Box,
    Text,
    Flex,
    Divider,
    Columns,
    Column,
    Button,
    NumberField,
    RadioButtons,
    useToast
} from '@locus-taxy/neo-ui';
import { TimeField } from 'common/components/neo/InlineFields/TimeField';
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import { type RescheduleConfig, type SlotShift } from './helper';

type Props = {
    rescheduleConfig: RescheduleConfig,
    setConfig: (
        path: string,
        value: boolean | string | number | SlotShift
    ) => void,
    generateSlots: () => void,
    getString: (key: string) => string
};

const FROM_SLOT = Object.freeze({
    start: 'fromSlotStart',
    end: 'fromSlotEnd'
});

const getRadioButtonOptions = (
    t: (key: string) => string
): Array<{ key: string, value: string }> => [
    {
        key: FROM_SLOT.start,
        value: t('radioOptions.previousSlotStartTime')
    },
    {
        key: FROM_SLOT.end,
        value: t('radioOptions.previousSlotEndTime')
    }
];

const AutoSlotGenerator = ({
    rescheduleConfig,
    setConfig,
    generateSlots,
    getString
}: Props): React.Node => {
    const {
        shift,
        shift: { startTime, endTime },
        slotWidthMins,
        overlapSlots,
        slotGapMins
    } = rescheduleConfig;

    const { showToast } = useToast();

    const [
        isSlotGenerationDisabled,
        setSlotGenerationDisabled
    ] = React.useState(false);

    const [gapType, setGapType] = React.useState(
        overlapSlots ? FROM_SLOT.start : FROM_SLOT.end
    );

    const canGenerateSlot = (): boolean => {
        return !!(
            startTime &&
            endTime &&
            !!slotWidthMins &&
            slotGapMins !== ''
        );
    };

    const hasAnyKnownError = (): { title: string, content: string } | null => {
        const title = getString('cannotGenerateSlots.label');

        if (gapType === FROM_SLOT.start && slotGapMins === 0) {
            return {
                title,
                content: getString('changeSlotGap.description')
            };
        }
        if (!canGenerateSlot) {
            return {
                title,
                content: getString('cannotGenerateSlots.description')
            };
        }

        return null;
    };

    const handleSlotGeneration = () => {
        // validation
        const anyKnownError = hasAnyKnownError();
        if (anyKnownError) {
            return showToast({
                title: anyKnownError.title,
                palette: 'danger',
                content: anyKnownError.content,
                anchorOrigin: { horizontal: 'right', vertical: 'bottom' }
            });
        }
        generateSlots();
        setSlotGenerationDisabled(true);

        // disable continuous clicking
        setTimeout(() => setSlotGenerationDisabled(false), 2000);
    };

    const handleRadioChange = ({ key }) => {
        setGapType(key);
        setConfig('overlapSlots', key === FROM_SLOT.start);
    };

    const setShiftStartTime = (time: string) => {
        setConfig('shift', { ...shift, startTime: time });
    };

    const setShiftEndTime = (time: string) => {
        setConfig('shift', { ...shift, endTime: time });
    };

    const handleSlotWidthChange = (time: number | '') =>
        setConfig('slotWidthMins', time);

    const handleSlotGapChange = (time: number | '') =>
        setConfig('slotGapMins', time);

    const minSlotEndTime = startTime
        ? TimeUtils.getMoment(startTime, DateTimeFormat.HTML5_FMT_TIME)
        : '';

    const maxSlotStartTime = endTime
        ? TimeUtils.getMoment(endTime, DateTimeFormat.HTML5_FMT_TIME)
        : '';

    return (
        <Box backgroundColor="white" padding={4} borderRadius={4}>
            <Box mb={2}>
                <Text fontSize={2} fontWeight="400" mb={2}>
                    {getString('generateSlots.label')}
                </Text>
                <Divider color="grey.200" />
                <Text fontSize={2} fontWeight="400" color="grey.600" pt={2}>
                    {getString('generateSlots.description')}
                </Text>
            </Box>
            <Columns space="medium">
                <Column>
                    <TimeField
                        label={getString('generateSlots.shiftStartTime')}
                        required
                        value={startTime}
                        onChange={setShiftStartTime}
                        fullWidth
                        max={maxSlotStartTime}
                    />
                </Column>
                <Column>
                    <TimeField
                        label={getString('generateSlots.shiftEndTime')}
                        required
                        value={endTime}
                        onChange={setShiftEndTime}
                        fullWidth
                        min={minSlotEndTime}
                    />
                </Column>
            </Columns>
            <Box my={4}>
                <NumberField
                    fullWidth
                    label={getString('generateSlots.howLongIsSlot')}
                    required
                    value={slotWidthMins}
                    onChange={handleSlotWidthChange}
                />
            </Box>
            <Text fontSize={2} fontWeight="400" color="grey.600">
                {getString('generateSlots.addGapLabel')}
            </Text>
            <Box my={4} px={2}>
                <RadioButtons
                    value={gapType}
                    size="medium"
                    label=""
                    options={getRadioButtonOptions(getString)}
                    onChange={handleRadioChange}
                    row
                />
            </Box>
            <NumberField
                fullWidth
                label={getString('generateSlots.gapBetweenSlots')}
                required
                value={slotGapMins}
                onChange={handleSlotGapChange}
            />
            <Flex justifyContent="flex-end" mt={4}>
                <Button
                    palette="primary"
                    appearance="ghost"
                    onClick={handleSlotGeneration}
                    disabled={isSlotGenerationDisabled}
                >
                    {getString('generateSlots.btnLabel')}
                </Button>
            </Flex>
        </Box>
    );
};

export default AutoSlotGenerator;
